import { h } from 'preact';
import type { PromoMetadata } from './usePromoCode';
import { COPY, appliedDiscountFor, suffixesFor } from './PromoBanner';
import { sanitizePromoHtml } from '../utils/sanitizeHtml';

/**
 * Stage-2 offer for the PDP (ORT-3060): a Shopware-maintained, not-yet-applied code shown
 * as "Sichere dir <Rabatt> mit Code <CODE>" plus an Anwenden button. Reuses PromoBanner's
 * discount interpretation (appliedDiscountFor / suffixesFor) so the wording stays in sync.
 * Applying is analog to Stage 1: the code is pre-stored and the price resolves in the cart.
 *
 * Visuals: .promo-banner--offer in scss/orthomol/components/eshop/_promo-banner.scss.
 */
interface PromoOfferProps {
  metadata: PromoMetadata;
  onApply: () => void;
}

export const PromoOffer = ({ metadata, onApply }: PromoOfferProps) => {
  const discount = appliedDiscountFor(metadata);
  const subline = (COPY.offerSubline + suffixesFor(metadata)).trim();
  // Shopware-maintained rich text (ORT-3110) replaces the composed headline when present.
  const userDescription = metadata.userDescription?.trim();

  return (
    <div className="promo-banner promo-banner--offer" role="region" aria-label="Rabattcode-Angebot">
      <div className="promo-banner__body">
        {userDescription ? (
          <span
            className="promo-banner__headline"
            dangerouslySetInnerHTML={{ __html: sanitizePromoHtml(userDescription) }}
          />
        ) : (
          <span className="promo-banner__headline">
            {COPY.offerHeadlinePrefix}
            <span className="promo-banner__headline-discount">
              <strong>{discount}</strong>
            </span>
            {COPY.offerHeadlineConnector}
            <strong>{metadata.code}</strong>
          </span>
        )}
        <span className="promo-banner__subline">{subline}</span>
      </div>
      <button type="button" className="promo-banner__apply btn btn-primary" onClick={onApply}>
        {COPY.offerApply}
      </button>
    </div>
  );
};
